/**
 * Blockscout Explorer API Client
 * Fetches contract data from Mezo Explorer (Blockscout) as external data source.
 * Used for read-only queries; falls back to RPC when API fails.
 */

import { MEZO_EXPLORER_URL } from '@/lib/boar-config';
import { MEZO_CONTRACTS } from '@/lib/mezo';

// In the browser we go through a relative proxy path (`/explorer-api`) to avoid
// CORS issues when calling the Blockscout instance. In non-browser/server
// environments we can talk to the explorer directly using its full URL.
const BASE_URL =
  typeof window === 'undefined'
    ? `${MEZO_EXPLORER_URL}/api/v2`
    : '/explorer-api';
const CACHE_TTL_MS = 5_000; // 5 seconds - ensures public data consistency (same for all users)
const REQUEST_TIMEOUT_MS = 20_000; // Increased timeout for reliability
const RETRY_DELAY_MS = 2_000;
const MAX_RETRIES = 2; // Increased retries

// Note: All data fetched here is PUBLIC blockchain data visible to everyone
// No user-specific or browser-specific caching that would show different data

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

function getCached<T>(key: string): T | null {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (!entry || Date.now() > entry.expiresAt) {
    cache.delete(key);
    return null;
  }
  // Add cache age for debugging
  const age = Date.now() - (entry.expiresAt - CACHE_TTL_MS);
  if (age > 0) {
    console.log(`[ExplorerAPI] Cache hit for ${key}, age: ${age}ms`);
  }
  return entry.data;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
}

async function fetchExplorer<T>(path: string): Promise<T | null> {
  const url = `${BASE_URL}${path}`;
  const cacheKey = `explorer:${path}`;
  const cached = getCached<T>(cacheKey);
  if (cached !== null) return cached;

  const data = await fetchExplorerNoCache<T>(path);
  if (data !== null) setCache(cacheKey, data);
  return data;
}

/** Fetch without cache (used for pagination loop). Retries once on failure. */
async function fetchExplorerNoCache<T>(path: string): Promise<T | null> {
  const url = `${BASE_URL}${path}`;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!res.ok) {
        console.warn(`[ExplorerAPI] ${path} returned ${res.status}${attempt < MAX_RETRIES ? ', retrying...' : ''}`);
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
        return null;
      }

      return (await res.json()) as T;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`[ExplorerAPI] Failed to fetch ${path}:`, msg, attempt < MAX_RETRIES ? '(retrying...)' : '');
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        continue;
      }
      return null;
    }
  }
  return null;
}

/** Transaction from Blockscout API */
export interface ExplorerTransaction {
  hash: string;
  from: { hash: string };
  to: { hash: string } | null;
  value: string;
  block: number;
  timestamp: string;
  status?: string;
  method?: string;
  decoded_input?: { method_id: string; parameters: unknown[] };
  logs?: Array<{
    decoded?: { name: string; inputs: Array<{ name: string; value: string }> };
    topics: string[];
    data?: string;
  }>;
}

/** Paginated transactions response (Blockscout uses 'items' or 'result') */
export interface ExplorerTransactionsResponse {
  items?: ExplorerTransaction[];
  result?: ExplorerTransaction[];
  next_page_params?: Record<string, string>;
}

/** Address info from Blockscout */
export interface ExplorerAddressInfo {
  hash: string;
  implementation_name?: string;
  is_contract?: boolean;
  creation_tx_hash?: string;
  token?: unknown;
}

/**
 * Fetch one page of transactions for an address.
 * Uses next_page_params for pagination (no cache).
 */
async function fetchAddressTransactionsPage(
  address: string,
  nextPageParams?: Record<string, string>
): Promise<ExplorerTransactionsResponse | null> {
  const params = new URLSearchParams();
  params.set('page_size', '50');
  if (nextPageParams) {
    for (const [k, v] of Object.entries(nextPageParams)) {
      params.set(k, v);
    }
  }
  const path = `/addresses/${address}/transactions?${params.toString()}`;
  return fetchExplorerNoCache<ExplorerTransactionsResponse>(path);
}

/**
 * Fetch transactions for an address (e.g. invoice contract).
 * Returns null on any error; caller should fall back to RPC.
 */
export async function fetchAddressTransactions(
  address: string,
  opts?: { page?: number; limit?: number }
): Promise<ExplorerTransactionsResponse | null> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('page_size', String(opts.limit));
  const qs = params.toString();
  const path = `/addresses/${address}/transactions${qs ? `?${qs}` : ''}`;
  return fetchExplorer<ExplorerTransactionsResponse>(path);
}

/**
 * Fetch all transactions for an address (all-time), paginating via next_page_params.
 */
export async function fetchAllAddressTransactions(
  address: string
): Promise<ExplorerTransactionsResponse | null> {
  const allItems: ExplorerTransaction[] = [];
  let nextPageParams: Record<string, string> | undefined;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    let res = await fetchAddressTransactionsPage(address, nextPageParams);
    if (!res) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      res = await fetchAddressTransactionsPage(address, nextPageParams);
    }
    if (!res) return null;

    const items = res.items ?? res.result ?? [];
    allItems.push(...items);

    const next = res.next_page_params;
    if (!next || typeof next !== 'object' || Object.keys(next).length === 0) {
      break;
    }
    nextPageParams = next as Record<string, string>;
  }

  return { items: allItems, next_page_params: undefined };
}

/**
 * Fetch address info (contract metadata).
 */
export async function fetchAddressInfo(
  address: string
): Promise<ExplorerAddressInfo | null> {
  return fetchExplorer<ExplorerAddressInfo>(`/addresses/${address}`);
}

/**
 * Fetch a single transaction by hash.
 */
export async function fetchTransaction(
  txHash: string
): Promise<ExplorerTransaction | null> {
  return fetchExplorer<ExplorerTransaction>(`/transactions/${txHash}`);
}

const INVOICE_TX_CACHE_KEY = 'explorer:invoice-contract:all';

/**
 * Fetch all transactions for the invoice contract (all-time, paginated).
 * Used to derive invoices/events when Explorer API is available.
 * Always fetches fresh data to ensure public consistency.
 */
export async function fetchInvoiceContractTransactions(forceFresh = false): Promise<ExplorerTransactionsResponse | null> {
  // Only use cache if not forcing fresh fetch
  if (!forceFresh) {
    const cached = getCached<ExplorerTransactionsResponse>(INVOICE_TX_CACHE_KEY);
    if (cached !== null) {
      console.log('[ExplorerAPI] Using cached data, age:', Date.now() - (cached as any).__cachedAt, 'ms');
      return cached;
    }
  }

  console.log('[ExplorerAPI] Fetching fresh transactions from Explorer for contract:', MEZO_CONTRACTS.INVOICE_CONTRACT);
  console.log('[ExplorerAPI] Explorer URL:', `${BASE_URL}/addresses/${MEZO_CONTRACTS.INVOICE_CONTRACT}/transactions`);
  const res = await fetchAllAddressTransactions(MEZO_CONTRACTS.INVOICE_CONTRACT);
  if (!res) {
    console.warn('[ExplorerAPI] Failed to fetch transactions from Explorer');
    return null;
  }

  const txCount = res.items?.length || res.result?.length || 0;
  console.log('[ExplorerAPI] ✅ Successfully fetched', txCount, 'transactions from Explorer');
  if (txCount > 0) {
    console.log('[ExplorerAPI] Transaction hashes:', (res.items || res.result || []).slice(0, 5).map((tx: ExplorerTransaction) => tx.hash));
  }
  // Add cache timestamp for debugging
  (res as any).__cachedAt = Date.now();
  setCache(INVOICE_TX_CACHE_KEY, res);
  return res;
}

/**
 * Clear explorer cache (e.g. after new invoice creation).
 */
export function clearExplorerCache(): void {
  cache.clear();
}

/**
 * Lightweight Explorer API health check (one page fetch).
 * Use on app init to verify Blockscout Explorer is reachable.
 */
export async function testExplorerConnection(): Promise<boolean> {
  try {
    const res = await fetchAddressTransactionsPage(MEZO_CONTRACTS.INVOICE_CONTRACT);
    return res !== null;
  } catch {
    return false;
  }
}
